// frontend/src/hooks/useExportJobs.js
//
// The store's generated exports: list them, and fetch one's bytes.
//
// An export past the server's row threshold is not a response — it is an
// ExportJob generated on the pipeline rail and downloaded afterwards
// (backend: core/models.py ExportJob, api/views/export_jobs.py). The button
// that asked for it polls the one job it created and downloads it inline, but
// that only works for as long as the merchant keeps the tab open. This list is
// the way back to a file whose originating tab is gone — which, for an export
// measured in minutes, is the normal case rather than the edge one.

import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthContext';
import { exportJobs as exportJobsKey } from '../queries/keys';
import { sessionApi } from '../services/SessionApiService';
import { fetchAllPagesVia } from '../utils/fetchAllPages';
import { shouldPoll, LIST_POLL_MS } from '../utils/exportJobs';

/**
 * The current store's generated exports, newest first.
 *
 * Walks every page rather than reading the first. The list is short — jobs
 * live 7 days — but "short" is an expectation, not a bound, and a merchant
 * whose oldest export fell off page 1 would be told it does not exist while
 * the file sits in the container. ``fetchAllPagesVia`` is loud at both ends:
 * it throws on a body that is not a DRF page, and again if the walk outruns
 * MAX_REFERENCE_PAGES, so an unexpectedly long list fails instead of
 * truncating. Either throw lands on the page's load-failed alert.
 *
 * Polls only while at least one job is still generating, so a page left open
 * on a settled list is not a load generator. Expired jobs never appear:
 * housekeeping deletes the row with the artifact, so "in this list" and
 * "downloadable" are the same statement (api/services/export_jobs.py
 * export_artifact_purge_step).
 */
export function useExportJobs() {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;

  return useQuery({
    queryKey: exportJobsKey.all(storeId),
    queryFn: () => fetchAllPagesVia(sessionApi, '/api/export-jobs/', {
      label: 'exports',
    }),
    enabled: !!storeId,
    refetchInterval: ({ state }) => (shouldPoll(state.data) ? LIST_POLL_MS : false),
  });
}
